import { useState, useEffect } from 'react';
import { patientsApi } from '@/api';
import type { PatientProfile } from '@/api';
import { Button } from '@medikei/ui';
import { Card } from '@medikei/ui';
import { Input } from '@medikei/ui';
import { Skeleton } from '@medikei/ui';
import { ReferralCard } from '@/components/referral';
import { useToast } from '@/providers/ToastProvider';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Save,
  Camera,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function PatientProfilePage() {
  const { toast } = useToast();
  const [profile, setProfile] = useState<PatientProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cpf: '',
    birthDate: '',
    gender: '',
    avatar: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await patientsApi.getProfile();
        setProfile(data);
        setFormData({
          name: data.name || '',
          phone: data.phone || '',
          cpf: data.cpf || '',
          birthDate: data.birthDate
            ? format(new Date(data.birthDate), 'yyyy-MM-dd')
            : '',
          gender: data.gender || '',
          avatar: data.avatar || '',
        });
      } catch {
        toast({ title: 'Erro', description: 'Não foi possível carregar o perfil.', variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updated = await patientsApi.updateProfile(formData);
      setProfile(updated);
      setIsEditing(false);
      toast({ title: 'Perfil atualizado', description: 'Suas informações foram salvas com sucesso.', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Erro', description: error?.message || 'Não foi possível salvar as alterações.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        phone: profile.phone || '',
        cpf: profile.cpf || '',
        birthDate: profile.birthDate
          ? format(new Date(profile.birthDate), 'yyyy-MM-dd')
          : '',
        gender: profile.gender || '',
        avatar: profile.avatar || '',
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Card className="p-6">
          <div className="flex items-start gap-6">
            <Skeleton className="h-24 w-24 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <User className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Perfil não encontrado</h2>
        <p className="text-muted-foreground">
          Não foi possível carregar suas informações.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Meu Perfil</h1>
        <p className="text-muted-foreground mt-1">
          Gerencie suas informações pessoais
        </p>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {formData.avatar ? (
                  <img
                    src={formData.avatar}
                    alt={formData.name || 'Usuário'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="h-10 w-10 text-primary" />
                )}
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 h-8 w-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90">
                  <Camera className="h-4 w-4" />
                </button>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                {formData.name || 'Paciente'}
              </h2>
              <p className="text-sm text-muted-foreground">{profile.email}</p>
            </div>
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>Editar perfil</Button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Nome completo</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Seu nome"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Telefone</label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">CPF</label>
                <Input
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleInputChange}
                  placeholder="000.000.000-00"
                  disabled
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Data de nascimento</label>
                <Input
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Gênero</label>
                <Input
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  placeholder="Gênero"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={isSaving}>
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Salvando...' : 'Salvar alterações'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">E-mail</p>
                  <p className="font-medium">{profile.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Telefone</p>
                  <p className="font-medium">{profile.phone || 'Não informado'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">CPF</p>
                  <p className="font-medium">{profile.cpf || 'Não informado'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Data de nascimento</p>
                  <p className="font-medium">
                    {profile.birthDate
                      ? format(new Date(profile.birthDate), "dd 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })
                      : 'Não informada'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      <ReferralCard />
    </div>
  );
}
